import axios from 'axios';
import queryString from 'query-string';
import * as Sentry from 'utils/errorTracking';
import { getAPI } from 'utils/helpers';
import { filterHiddenOfferings } from 'utils/offerings/hiddenSlug';

/**
 * PD-35613: drop offerings whose slug is in the
 * `NEXT_PUBLIC_HIDDEN_OFFERING_SLUGS` env var from any elastic-search
 * response *before* it reaches consumers (search results page,
 * top-nav search dropdown, partner offerings, homepage V2 panels,
 * sitemap generator). Applying the filter at the service layer means
 * we can't accidentally forget a call site, and we don't change the
 * shape of the response — we only shrink the offerings array.
 *
 * Pagination metadata (`pageNumber`, `totalPages`) is left alone: the
 * elastic backend's totals reflect the un-filtered index, so a result
 * page with N hits may end up showing N-1 if one hidden slug was on
 * it. Acceptable for a 0-3 slug list; if the list grows we should
 * migrate to the proper backend `hidden` flag so elastic itself
 * excludes them at index time.
 */
function applyHiddenFilterToSearch(data) {
  if (!data || !Array.isArray(data.list)) return data;
  const filtered = filterHiddenOfferings(data.list);
  if (filtered === data.list) return data;
  return { ...data, list: filtered };
}

function applyHiddenFilterToSuggestions(data) {
  if (!data || !Array.isArray(data.offerings)) return data;
  const filtered = filterHiddenOfferings(data.offerings);
  if (filtered === data.offerings) return data;
  return { ...data, offerings: filtered };
}

export const getElasticSuggestions = ({
  pageParam = 0,
  query = '',
  size = 10,
  fingerprint = '',
}) => {
  return axios
    .get(
      queryString.stringifyUrl({
        url: `${getAPI()}/elastic-search/search/offering/suggestions`,
        query: {
          page: pageParam,
          query,
          size,
          fingerprint,
        },
      }),
    )
    .then(({ data }) => applyHiddenFilterToSuggestions(data));
};

export const getElasticOfferingSearch = async ({
  page,
  query = '',
  size = 10,
  categoryLabel,
  sortBy,
  statuses,
  fingerprint,
  regulationTypes,
  partners,
}) => {
  const combinedRegulationTypes = Array.isArray(regulationTypes)
    ? regulationTypes.join(',')
    : regulationTypes;

  try {
    const { data } = await axios.get(
      queryString.stringifyUrl({
        url: `${getAPI()}/elastic-search/search/offering/search`,
        query: {
          page,
          query,
          statuses,
          size,
          categories: categoryLabel,
          sortBy: sortBy || 'mostMomentum',
          fingerprint: fingerprint || null,
          regulationTypes: combinedRegulationTypes || null,
          partners,
        },
      }),
    );
    return applyHiddenFilterToSearch(data);
  } catch (error) {
    Sentry.captureException(error, { tags: { service: 'elastic-search' } });
    return { list: [], pageNumber: 0, totalPages: 0, aggregations: {} };
  }
};

export const getSearchHistory = ({ limit, fingerprint }) => {
  return axios
    .get(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/elastic-search/search/offering/search/history`,
          query: {
            limit,
            fingerprint: fingerprint || null,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
    )
    .then(({ data }) => data);
};

export const postSearchHistory = ({ query, fingerprint }) => {
  return axios
    .post(
      queryString.stringifyUrl(
        {
          url: `${getAPI()}/elastic-search/search/offering/search/history`,
          query: {
            query,
            fingerprint: fingerprint || null,
          },
        },
        {
          skipEmptyString: true,
        },
      ),
      {},
    )
    .then(({ data }) => data);
};
